import React, { useState, useEffect } from 'react';

import { useGetBlogsQuery } from '../../features/blogApiSlice';
import { useSelector } from 'react-redux';
import { selectCurrentLanguage } from '../../features/langSlice';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { BasedOnYourDiet, OurIngredients, YourPersonalizedFormula } from '../../Constants/index';
import CustomLink from '../CustomLink';
import RiisePouch from '../../assets/riise-pouch.png';
import MicrogranuleImg from '../../assets/microgranules-technology-what-it-is.webp';
import { Icon } from '../elements/icon';

const Ingredients = () => {
  const selectedLang = useSelector(selectCurrentLanguage);
  const [lang, setLang] = useState();
  const { t } = useTranslation();
  const [data, setData] = useState([]);

  const {
    data: fetchBlogs,
    isSuccess,
    refetch,
  } = useGetBlogsQuery({
    lang: lang,
    page: 1,
    rows: 100,
    category: 'ingredient',
  });
  console.log('fetchBlogs', fetchBlogs);

  useEffect(() => {
    setLang(selectedLang === 'us' ? 'en' : selectedLang);

    refetch();
  }, [refetch, lang, selectedLang]);

  useEffect(() => {
    if (isSuccess) {
      setData(fetchBlogs.data);
    }
  }, [fetchBlogs, isSuccess]);

  return (
    <>
      <Helmet>
        <title>Riise Ingredients</title>
        <meta name="description" content="Riise ingredient" />
      </Helmet>
      <section
        className="t-w-full"
        style={{ backgroundColor: '#1a4a4a', minHeight: '200px' }}
      >
        <div className="container t-py-12">
          <div className="t-grid t-grid-cols-1 md:t-grid-cols-2 t-gap-8 t-items-center">
            {/* Left column */}
            <div>
              <div
                style={{
                  fontSize: '11px',
                  textTransform: 'uppercase',
                  letterSpacing: '1.5px',
                  color: '#5DCAA5',
                  marginBottom: '12px',
                }}
              >
                {t('IngredientsHero_Eyebrow')}
              </div>
              <h1
                style={{
                  fontSize: '28px',
                  fontWeight: 700,
                  color: '#f5f0e8',
                  margin: 0,
                  lineHeight: 1.2,
                }}
              >
                {t('IngredientsHero_TitlePart1')}{' '}
                <em style={{ color: '#f5c842', fontStyle: 'italic', fontWeight: 700 }}>
                  {t('IngredientsHero_TitlePart2')}
                </em>
              </h1>
              {/* Desktop subtitle */}
              <p
                className="t-hidden md:t-block"
                style={{
                  fontSize: '13px',
                  color: 'rgba(245,240,232,0.65)',
                  maxWidth: '380px',
                  marginTop: '16px',
                  marginBottom: '20px',
                  lineHeight: 1.5,
                }}
              >
                {t('IngredientsHero_Subtitle')}
              </p>

              {/* Mobile-only: shorter subtitle + microgranule card */}
              <div className="md:t-hidden">
                <p
                  style={{
                    fontSize: '14px',
                    color: 'rgba(245,240,232,0.65)',
                    lineHeight: 1.5,
                    marginTop: '16px',
                    marginBottom: '16px',
                  }}
                >
                  {t('IngredientsHero_SubtitleShort')}
                </p>
                <CustomLink
                  to="/ingredients/microgranule"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '14px',
                    padding: '14px',
                    border: '1px solid rgba(245,240,232,0.15)',
                    borderRadius: '12px',
                    marginBottom: '20px',
                    textDecoration: 'none',
                  }}
                >
                  <img
                    src={MicrogranuleImg}
                    alt=""
                    style={{
                      width: '84px',
                      height: '84px',
                      borderRadius: '50%',
                      objectFit: 'cover',
                      flexShrink: 0,
                    }}
                  />
                  <div style={{ flex: 1 }}>
                    <div
                      style={{
                        fontSize: '15px',
                        fontWeight: 700,
                        color: '#f5f0e8',
                        marginBottom: '4px',
                      }}
                    >
                      {t('IngredientsHero_CardTitle')}
                    </div>
                    <div
                      style={{
                        fontSize: '13px',
                        color: 'rgba(245,240,232,0.6)',
                        lineHeight: 1.4,
                        marginBottom: '6px',
                      }}
                    >
                      {t('IngredientsHero_CardBody')}
                    </div>
                    <span style={{ fontSize: '13px', color: '#f5c842' }}>
                      {t('IngredientsHero_LearnMore')}
                    </span>
                  </div>
                </CustomLink>
              </div>
              <div className="t-flex t-flex-wrap t-gap-2">
                {[
                  'IngredientsHero_PillVitamins',
                  'IngredientsHero_PillMinerals',
                  'IngredientsHero_PillAdaptogens',
                  'IngredientsHero_PillFattyAcids',
                  'IngredientsHero_PillAminoAcids',
                ].map((key) => (
                    <span
                      key={key}
                      style={{
                        fontSize: '11px',
                        border: '1px solid rgba(245,240,232,0.2)',
                        color: 'rgba(245,240,232,0.7)',
                        borderRadius: '20px',
                        padding: '4px 12px',
                      }}
                    >
                      {t(key)}
                    </span>
                  ))}
              </div>
            </div>

            {/* Right column (desktop only) */}
            <div className="t-hidden md:t-flex t-flex-col t-items-center md:t-items-end">
              <div
                style={{
                  width: '220px',
                  height: '220px',
                  borderRadius: '50%',
                  overflow: 'hidden',
                  border: '1px solid rgba(245,240,232,0.15)',
                }}
              >
                <img
                  src={MicrogranuleImg}
                  alt="Riise microgranules close-up"
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              </div>
              <p
                style={{
                  marginTop: '16px',
                  marginBottom: 0,
                  fontSize: '14px',
                  fontWeight: 600,
                  color: '#f5f0e8',
                  textAlign: 'center',
                }}
              >
                {t('IngredientsHero_TechTitle')}
              </p>
              <CustomLink
                to="/ingredients/microgranule"
                style={{
                  marginTop: '8px',
                  fontSize: '12px',
                  color: '#f5c842',
                  textTransform: 'uppercase',
                  letterSpacing: '1px',
                  textDecoration: 'none',
                  borderBottom: '1px solid #f5c842',
                  paddingBottom: '2px',
                }}
              >
                {t('IngredientsHero_LearnMore')}
              </CustomLink>
            </div>
          </div>
        </div>
      </section>
      <section className="services blogs section-padding t-min-h-[100vh]">
        <div className="container">
          <div className="t-grid t-grid-cols-2 md:t-grid-cols-3 lg:t-grid-cols-4 t-gap-4">
            {data.filter((ingred) => ingred.slug !== 'microgranule').map((ingred, index) => (
              <div className="t-bg-white t-overflow-hidden t-align-top t-flex t-flex-col" key={index}>
                <div className="t-w-full t-aspect-square t-relative t-rounded-lg t-overflow-hidden">
                  <CustomLink to={`/ingredients/${ingred.slug}`}>
                    <img
                      src={`${process.env.REACT_APP_PUBLIC_URL}${ingred.picture}`}
                      alt={ingred.title}
                      className="t-absolute t-inset-0 t-h-full t-object-cover t-rounded-lg" // t-w-full
                    />
                  </CustomLink>
                </div>
                <div className="t-mt-3" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                  <CustomLink to={`/ingredients/${ingred.slug}`}>
                    <h4
                      style={{
                        fontFamily: "'DM Serif Display', serif",
                        fontSize: '20px',
                        fontWeight: 400,
                        color: 'var(--navy, #103642)',
                        margin: 0,
                        lineHeight: 1.2,
                      }}
                    >
                      {ingred.title}
                    </h4>
                  </CustomLink>
                  <CustomLink to={`/ingredients/${ingred.slug}`}>
                    <div
                      style={{
                        fontSize: '13px',
                        color: 'rgba(16,54,66,0.7)',
                        marginTop: '6px',
                        lineHeight: 1.5,
                      }}
                    >
                      <span dangerouslySetInnerHTML={{ __html: ingred.contents }} />
                      <span style={{ color: 'var(--teal, #116D6E)', fontWeight: 600 }}>
                        ... {t('IngredientsGrid_ReadMore')}
                      </span>
                    </div>
                  </CustomLink>
                </div>
              </div>
            ))}
            <CustomLink
              to={`/quiz`}
              className="t-bg-gray-100 t-overflow-hidden t-align-top t-flex t-flex-col t-rounded-lg t-p-4"
            >
              <div className="t-w-full t-aspect-square t-relative t-rounded-lg t-overflow-hidden">
                <img
                  src={RiisePouch}
                  alt=""
                  className="t-absolute t-inset-0 t-h-full t-object-cover t-rounded-full" // t-w-full
                />
              </div>
              <div className="t-text-sm t-text-black t-mt-2">
                <h4 className="t-text-lg t-font-semibold t-text-secondary">{t(YourPersonalizedFormula)}</h4>

                {t(BasedOnYourDiet)}
                <div className="!t-w-10 !t-h-10 t-bg-quaternary !t-rounded-full t-flex t-items-center t-justify-center t-ml-auto">
                  <Icon name="arrowRight" color="#fff" size="28" />
                </div>
              </div>
            </CustomLink>
          </div>
        </div>
      </section>
    </>
  );
};

export default Ingredients;
